import AddressAutocomplete, { type IParsedAddress } from '@orn/shared-web/components/AddressAutocomplete';
import Button from '@orn/shared-web/components/Button/Button';
import { CloseIcon } from '@orn/shared-web/icons';

import { isCompleteAddress, toResidenceAddress } from '../../../../lib/dependents/residenceAddress';

import { type IFrequentPlace } from './residenceDraft';

interface IFrequentLocationsFormProps {
  places: IFrequentPlace[];
  onChange: (next: IFrequentPlace[]) => void;
  apiKey: string;
}

/**
 * The "frequently spend time elsewhere" sub-form — an array of free-text
 * address inputs with add/remove. The first row always renders (no remove
 * button); subsequent rows get a small ✕. Each row requires a real Places pick
 * that resolves to a city; a city-less pick surfaces an inline error rather
 * than silently disabling Continue. Purely presentational.
 */
const FrequentLocationsForm = ({ places, onChange, apiKey }: IFrequentLocationsFormProps) => {
  const setRow = (i: number, patch: Partial<IFrequentPlace>) => {
    const next = [...places];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  // Editing after a pick invalidates the structured selection until the user
  // chooses another suggestion — frequent locations require a real Places pick.
  const onText = (i: number, text: string) =>
    setRow(i, { text, address: places[i].address?.formatted === text ? places[i].address : null });
  const onPick = (i: number, parsed: IParsedAddress) =>
    setRow(i, { text: parsed.formattedAddress, address: toResidenceAddress(parsed) });
  const remove = (i: number) => onChange(places.filter((_, idx) => idx !== i));
  const add = () => onChange([...places, { id: null, text: '', address: null }]);

  return (
    <div className="border-secondary flex flex-col gap-3 rounded-xl border p-3">
      {places.map((place, i) => {
        // A picked row that came back without a city can't be saved — surface
        // why inline rather than leaving Continue mysteriously disabled.
        const needsCity = place.address !== null && !isCompleteAddress(place.address);
        return (
          // The id is stable for existing rows (use it); new rows fall back to
          // the index since they have no upstream identity yet.
          <div key={place.id ?? `new-${i}`} className="flex flex-col gap-1">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <AddressAutocomplete
                  apiKey={apiKey}
                  label={i === 0 ? 'Location' : undefined}
                  placeholder="City, State"
                  value={place.text}
                  // City- or street-level so a frequent location can be a bare
                  // city or a precise address — both types always resolve a
                  // city, keeping city-less regions ("Lake Tahoe") out.
                  includedPrimaryTypes={['street_address', 'locality']}
                  onInputChange={(v) => onText(i, v)}
                  onSelect={(parsed) => onPick(i, parsed)}
                />
              </div>
              {i > 0 && (
                <Button
                  color="secondary"
                  size="sm"
                  iconLeading={CloseIcon}
                  aria-label="Remove location"
                  onClick={() => remove(i)}
                  className="mb-1"
                />
              )}
            </div>
            {needsCity && (
              <p className="text-error-primary text-sm font-medium">
                Pick a location with a city, like a town or neighborhood.
              </p>
            )}
          </div>
        );
      })}
      <Button color="link-color" size="sm" onClick={add} className="self-start">
        + Add another location
      </Button>
    </div>
  );
};

FrequentLocationsForm.displayName = 'FrequentLocationsForm';

export default FrequentLocationsForm;
